import { Button, Text } from '@/components/ui';
import { Table } from '@/components/ui/Table';
import { PlusIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';
import { Group } from '@/lib/api/groups';
import { Switch } from '@/components/ui/Switch';
import { Actions } from './components/Actions';
import { Loader } from '@/components/Loader';
import { useState, useMemo } from 'react';
import { useGroups } from '@/hooks/useGroups';
import { CreateGroupModal } from './components/CreateGroupModal';
import { UpdateGroupModal } from './components/UpdateGroupModal';
import { Pagination } from '@/components/ui/Pagination';

export function GroupsPage() {
  const [newModalOpen, setNewModalOpen] = useState(false);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);

  const { t } = useTranslation();
  const {
    groups,
    pagination,
    isLoading,
    isSaving,
    createGroup,
    updateGroup,
    deleteGroup,
    refetch,
  } = useGroups({
    page: 1,
    limit: 10,
  });

  const handleEditGroup = (group: Group) => {
    setSelectedGroup(group);
    setUpdateModalOpen(true);
  };

  const confirmDeleteGroup = async (groupId: string) => {
    const success = await deleteGroup(groupId);
    if (success) {
      setSelectedGroup(null);
    }
  };

  const handlePageChange = (page: number) => {
    refetch({ page, limit: pagination.limit });
  };

  const columns: ColumnDef<Group>[] = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: t('groups.table.name'),
      },
      {
        accessorKey: 'description',
        header: t('groups.table.description'),
        cell: ({ row: { original } }) => original.description || '-',
      },
      {
        header: t('groups.table.active'),
        cell: ({ row: { original } }) => (
          <Switch
            checked={original.isActive}
            onCheckedChange={(checked) => updateGroup(original.id, { isActive: checked })}
            disabled={isSaving}
          />
        ),
      },
      {
        id: 'actions',
        cell: ({ row: { original } }) => (
          <Actions
            group={original}
            onEdit={handleEditGroup}
            onDelete={() => confirmDeleteGroup(original.id)}
            isLoading={isSaving}
          />
        ),
      },
    ],
    [updateGroup, isSaving, handleEditGroup, confirmDeleteGroup]
  );

  return (
    <div className="gap-2xl flex flex-1 flex-col">
      <Loader isLoading={isLoading} />
      <CreateGroupModal
        open={newModalOpen}
        onOpenChange={setNewModalOpen}
        onCreate={createGroup}
        isCreating={isSaving}
      />
      {selectedGroup && (
        <UpdateGroupModal
          open={updateModalOpen}
          onOpenChange={(open) => {
            setUpdateModalOpen(open);
            if (!open) setSelectedGroup(null);
          }}
          group={selectedGroup}
          onUpdate={updateGroup}
          isUpdating={isSaving}
        />
      )}

      <header className="flex w-full items-center justify-between">
        <Text as="h1" variant="heading">
          {t('groups.title')}
        </Text>
        <div>
          <Button onClick={() => setNewModalOpen(true)} disabled={isLoading}>
            <PlusIcon size={20} />
            {t('common.create')}
          </Button>
        </div>
      </header>

      <Table data={groups} columns={columns} />
      <Pagination
        className="mt-lg"
        currentPage={pagination.page}
        totalPages={pagination.totalPages}
        onPageChange={handlePageChange}
        disabled={isLoading}
      />
    </div>
  );
}
